/**
 * FloorPlanConfig — PF v2 native configuration UI (Wave 2, module 2c).
 *
 * The biggest Wave-2 module: a full configurator (saved-template grid + apply-
 * template writer + 2D/3D live preview + design/layout sections + company-logo
 * upload). Lifts the presentational markup from the legacy
 * `FloorPlanConfigStepController` sub-tree but rewrites ALL data wiring onto the
 * PF v2 store (`useFloorPlanConfigDraft`) + the file hub (`useFloorPlanLogoSync`).
 * No constate provider, no imperative handle, no legacy controller.
 *
 * Headline: the company logo now rides the file hub (`floorPlanLogo` kind) —
 * closing the LAST non-FileAPI upload in PF v2. F-B1 dies by construction on the
 * upload axis via the interaction-gated + value-guarded projection→DTO sync (§4b).
 *
 * Dropped vs. the legacy controller:
 *   - the cart 3D-editing-extra attach/detach effect (`FLOOR_PLAN_EDITING_3D` on
 *     2D↔3D toggle) — v2 has no cart-extras contract → DF-cart3d [REVENUE], same
 *     class as measurement DF-1 / FPC DF-6.
 *   - nothing on the analytics axis any more: the configuration page announces
 *     this tab, `useFloorPlanChoiceAnnouncer` carries all five legacy per-choice
 *     events including `FLOOR_PLAN_LOGO_UPLOADED`, and Save carries
 *     `floor_plan_options_selected`.
 *   - the live `isTemplateLogoUploading` completeness gate → dropped from the pure
 *     predicate; a UI guard disables the template grid/create while a logo
 *     transfers (DF-logo-uploading-gate).
 *   - `StepWrapper` step title/subtitle (a `PurchaseFlow/*` import) → the v2 page
 *     owns the page title; a section header keeps parity where needed.
 *
 * ZERO imports from `components/pages/PurchaseFlow/*` (DoD grep gate). Reuses the
 * legacy `purchase_flow:floor_plan_step.*` i18n namespace verbatim.
 *
 * Keeps the legacy layout: the live 2D/3D preview panel (sticky, left) beside the form.
 */

import React, { useCallback, useEffect } from 'react'
import { Box, Fade, Stack, useMediaQuery } from '@mui/material'
import { MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { useTranslation } from 'react-i18next'

import { MUIInputField } from 'components/common/MUIInputField'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SystemMessage } from 'components/common/SystemMessage'
import { useUserData } from 'components/contexts/UserDataContext'
import { Language } from 'translations/Language'

import type { CartItemId } from 'models/purchaseFlowRevamp'
import type { CartMutationHandler } from '../../adapters/bridge'

import { FLOOR_PLAN_LINE_SETTINGS_KEY, type FloorPlanConfigDraftDTO } from './floorPlanConfig.dto'
import {
  FloorPlanColors,
  FloorPlanFileOrientation,
  FloorPlanFormat,
  FloorPlanRenderType,
  FloorPlanSelectionType,
  Orientations,
  Styles
} from 'constants/floorPlan'
import { useFloorPlanConfigDraft } from './useFloorPlanConfigDraft'
import { useFloorPlanLineIds } from './useFloorPlanLineIds'
import { useFloorPlanLogoSync } from './useFloorPlanLogoSync'
import { useFloorPlanChoiceAnnouncer } from './useFloorPlanChoiceAnnouncer'
import { useFloorPlan3DExtraSync } from './useFloorPlan3DExtraSync'
import { useFloorPlanProductFacts } from './useFloorPlanProductFacts'
import { FloorPlanSourceSection } from './sections/FloorPlanSourceSection.component'
import { useFloorPlanConfigTemplates } from './useFloorPlanConfigTemplates'
import { FloorPlanPreview2D, FloorPlanPreview3D, type FloorPlanPreviewOverlays } from 'components/common/FloorPlanControls'
import type { FloorPlanConfigOptions } from 'constants/floorPlan'
import { FloorPlanDesignSection } from './sections/FloorPlanDesignSection.component'
import { FloorPlanLayoutSection } from './sections/FloorPlanLayoutSection.component'
import { FloorPlanTemplateSelection } from './sections/FloorPlanTemplateSelection.component'
import { FloorPlanRenderTypeSelect } from './sections/FloorPlanRenderTypeSelect.component'

export interface FloorPlanConfigProps {
  cartItemId: CartItemId
  /** Accepted for registry signature uniformity — floorPlanConfig emits no cart
   *  mutations (the cart 3D-editing-extra effect is dropped, DF-cart3d). */
  onCartMutation?: CartMutationHandler
}

/** Fields reset when creating a config from scratch (legacy `resetConfiguration`). */
/** The draft's overlay switches, in the shape the shared preview takes. */
const previewOverlays = (value: FloorPlanConfigDraftDTO): FloorPlanPreviewOverlays => ({
  showFixtures: value.fixtures,
  showFurniture: value.furniture,
  showRoomNames: value.roomNames,
  showScalebar: value.scaleBar,
  showCompass: value.compass,
  showDisclaimer: value.showDisclaimer,
  showLogo: !!value.isActiveLogo && !!value.companyLogo,
  isActiveDimensions: value.isActiveDimensions,
  showDimensionChain: value.dimensions?.showDimensionChain ?? false,
  showRoomArea: value.dimensions?.showEachRoomArea ?? false,
})

const RESET_SUBSET: Partial<FloorPlanConfigDraftDTO> = {
  selectedTemplateId: null,
  gardensAndRoofSlopes: false,
  style: FloorPlanColors.BLACK,
  selectedPropertyType: null,
  fixtures: false,
  furniture: false,
  orientation: undefined,
  roomNames: false,
  scaleBar: false,
  compass: false,
  isActiveDimensions: false,
  dimensions: { showDimensionChain: false, showEachRoomArea: false },
  outputFileLanguage: Language.EN,
  outputFileFormat: [FloorPlanFormat.JPG],
  isActiveLogo: false,
  fileOrientation: [FloorPlanFileOrientation.HORIZONTAL],
  isMultiStoryPlan: false,
  companyLogo: null,
  showDisclaimer: false,
  disclaimer: '',
  clientComment: '',
}

/**
 * @component FloorPlanConfig
 * Native floor-plan configurator. Reads/writes `productConfigs` via
 * `useFloorPlanConfigDraft`; the logo uploads through the PF v2 file hub.
 */
export const FloorPlanConfig: React.FC<FloorPlanConfigProps> = ({ cartItemId, onCartMutation }) => {
  const { t } = useTranslation(['purchase_flow', 'purchase_flow_revamp'])
  const isMobile = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)

  const { value, patch } = useFloorPlanConfigDraft(cartItemId)
  const floorPlanLineCount = useFloorPlanLineIds().length
  const { logo, hasRestoredLogo, uploadLogo, deleteLogo, clearLogo } = useFloorPlanLogoSync(cartItemId)
  const { is3DAllowed, isEditing, requiresSource, inputImagePolicy } =
    useFloorPlanProductFacts(cartItemId)
  const { availableTemplates, isFetching } = useFloorPlanConfigTemplates(is3DAllowed)

  // Legacy defaulted the output language to the (target) user's locale — `resetConfiguration`
  // set `Language[targetUser.language] ?? EN`. Native was forcing EN on every create/apply →
  // a DE user got EN-labelled plans by default (verify-fpconfig SHOULD-FIX). Client-self happy
  // path uses the current user's locale; admin-on-behalf has no resolvable target locale → EN.
  const { baseUserData } = useUserData()
  const defaultLanguage = Language[baseUserData?.language as Language] ?? Language.EN

  const templateType = value.floorPlanTemplateType
  const is2D = value.currentFloorPlanRenderType === FloorPlanRenderType.RENDER_2D
  const is3D = value.currentFloorPlanRenderType === FloorPlanRenderType.RENDER_3D

  // An editing line has to say what to draw from the client's plan, so it starts with the question unanswered.
  const hasEditingScope = Object.keys(value.floorPlanProductConfig ?? {}).length > 0
  useEffect(() => {
    if (!isEditing || hasEditingScope) return
    patch({ floorPlanProductConfig: { [FLOOR_PLAN_LINE_SETTINGS_KEY]: { drawOnlySpecificUnit: undefined, drawOnlySpecificUnitComment: '' } } })
  }, [isEditing, hasEditingScope, patch])

  // 3D is a priced upgrade, not a styling choice — the cart line has to carry it.
  useFloorPlan3DExtraSync(onCartMutation)

  // Legacy measured this form choice by choice; `logo.entries.length` is what
  // stands in for its "the uploaded count went up" rule.
  const { announce: announceFloorPlanChoice } = useFloorPlanChoiceAnnouncer(cartItemId, logo.entries.length)

  // ── Apply a saved template = ONE patch (+ optional bundled-logo upload) ─────────
  const applyTemplate = useCallback(
    (templateId: string, config: FloorPlanConfigOptions, renderType: FloorPlanRenderType) => {
      const is2DTemplate = renderType === FloorPlanRenderType.RENDER_2D
      const style = is2DTemplate ? (config.style || FloorPlanColors.BLACK) : config.style
      const hasLogo = !!(config.companyLogo && config.templateLogoFile)

      const next: Partial<FloorPlanConfigDraftDTO> = {
        selectedTemplateId: templateId,
        floorPlanTemplateType: FloorPlanSelectionType.TEMPLATE,
        currentFloorPlanRenderType: renderType,
        style,
        orientation: is2DTemplate ? undefined : config.orientation,
        fixtures: config.showFixtures ?? false,
        furniture: config.showFurniture ?? false,
        roomNames: config.showRoomNames ?? false,
        scaleBar: config.showScalebar ?? false,
        compass: config.showCompass ?? false,
        gardensAndRoofSlopes: config.showGardensAndRoofSlopes ?? false,
        isActiveDimensions: !!(config.showDimensionChain || config.showRoomArea),
        dimensions: {
          showDimensionChain: config.showDimensionChain ?? false,
          showEachRoomArea: config.showRoomArea ?? false,
        },
        outputFileFormat: config.floorPlanFormat ? Object.keys(config.floorPlanFormat) : [FloorPlanFormat.JPG],
        outputFileLanguage: config.outputLanguage ?? defaultLanguage,
        fileOrientation: is2DTemplate
          ? Array.from(config.fileOrientation ?? new Set([FloorPlanFileOrientation.HORIZONTAL]))
          : [],
        showDisclaimer: config.showDisclaimer ?? false,
        disclaimer: config.disclaimerText ?? '',
        isActiveLogo: hasLogo,
        companyLogo: hasLogo ? (config.templateLogoFile?.name ?? null) : null,
      }
      patch(next)

      // Route the template's bundled logo through the hub (arms `touched`, §4b).
      // The §4b sync then confirms `companyLogo` (value-guard → no-op vs the patch).
      if (hasLogo && config.templateLogoFile) uploadLogo([config.templateLogoFile])

      announceFloorPlanChoice('template', templateId, renderType)
    },
    [patch, uploadLogo, defaultLanguage, announceFloorPlanChoice]
  )

  // ── Create from scratch (legacy resetConfiguration + CUSTOM) ────────────────────
  const createFromScratch = useCallback(() => {
    if (logo.isUploading) return // UI guard — don't reset mid-upload
    patch({
      ...RESET_SUBSET,
      outputFileLanguage: defaultLanguage, // legacy resetConfiguration used the user's locale, not hardcoded EN
      floorPlanTemplateType: FloorPlanSelectionType.CUSTOM,
      currentFloorPlanRenderType: FloorPlanRenderType.RENDER_2D,
    })
    // Drop any previously-applied logo from the hub too — else the armed §4b sync
    // re-patches companyLogo back from the stale projection (verify-fpconfig MUST-FIX).
    clearLogo()
    announceFloorPlanChoice('create_from_scratch')
  }, [patch, logo.isUploading, clearLogo, defaultLanguage, announceFloorPlanChoice])

  // ── 2D/3D pick — single `style`/`orientation` field, so switch to that mode's
  //    default (legacy kept selectedColor + selectedStyle separately; minor delta) ─
  const selectRenderType = useCallback(
    (renderType: FloorPlanRenderType) => {
      const to3D = renderType === FloorPlanRenderType.RENDER_3D
      patch({
        currentFloorPlanRenderType: renderType,
        outputFileFormat: [FloorPlanFormat.JPG],
        style: to3D ? Styles.STYLE_1 : FloorPlanColors.BLACK,
        orientation: to3D ? Orientations.BIRD_VIEW : undefined,
        fileOrientation: to3D ? [] : [FloorPlanFileOrientation.HORIZONTAL],
      })
      announceFloorPlanChoice('render_type', renderType)
    },
    [patch, announceFloorPlanChoice]
  )

  const goBackToTemplateSelection = useCallback(() => {
    patch({ floorPlanTemplateType: null, selectedTemplateId: null, selectedPropertyType: null })
  }, [patch])

  return (
    <Fade in>
      <Stack direction={isMobile ? 'column' : 'row'} gap={isMobile ? 2 : 4} width="100%" justifyContent="space-between" alignItems={isMobile ? 'stretch' : 'flex-start'}>

        {/* Live preview: a sticky left column, above the form on mobile */}
        <Box top="-0.4rem" height="100%" flex={isMobile ? '0 0 auto' : '1 0 300px'} position={isMobile ? 'static' : 'sticky'}>

          {is2D && <FloorPlanPreview2D {...previewOverlays(value)} color={value.style} templateId={value.selectedTemplateId} />}

          {is3D && <FloorPlanPreview3D {...previewOverlays(value)} style={value.style} orientation={value.orientation} />}

        </Box>

        {/* Config column */}
        <Stack flex="1 1 75%" gap={2} minWidth={0}>

          {/* The plan the product is drawn from leads the column, as it led the
              drawer before the move: the settings below are chosen FOR it. */}
          {floorPlanLineCount > 1 &&
            <SystemMessage
              variant="info"
              message={t('purchase_flow_revamp:configuration.floor_plan.shared_settings_notice', { count: floorPlanLineCount })}
            />
          }

          <FloorPlanSourceSection cartItemId={cartItemId} requiresSource={requiresSource} policy={inputImagePolicy} />

          {/* Only where the logo control is shown: the client can do nothing about it anywhere else. */}
          {hasRestoredLogo && !!templateType && is2D &&
            <SystemMessage
              variant="warning"
              message={t('purchase_flow_revamp:configuration.floor_plan.logo_session_orphan_warning')}
            />
          }

          {templateType !== FloorPlanSelectionType.CUSTOM &&
            <FloorPlanTemplateSelection
              availableTemplates={availableTemplates}
              isFetching={isFetching}
              selectedTemplateId={value.selectedTemplateId}
              floorPlanTemplateType={templateType}
              logoUploading={logo.isUploading}
              applyTemplate={applyTemplate}
              createFromScratch={createFromScratch}
            />
          }

          {templateType === FloorPlanSelectionType.CUSTOM &&
            <FloorPlanRenderTypeSelect
              is3DAllowed={is3DAllowed}
              currentRenderType={value.currentFloorPlanRenderType ?? FloorPlanRenderType.RENDER_2D}
              selectRenderType={selectRenderType}
              goBack={goBackToTemplateSelection}
            />
          }

          {templateType && <>

            <FloorPlanDesignSection
              value={value}
              patch={patch}
              isEditing={isEditing}
              onColorSelected={(color) => announceFloorPlanChoice('color', color)}
            />

            {is2D &&
              <FloorPlanLayoutSection
                value={value}
                patch={patch}
                isEditing={isEditing}
                logoEntries={logo.entries}
                uploadLogo={uploadLogo}
                deleteLogo={deleteLogo}
              />
            }

            <SectionedBorderBox title={t('floor_plan_step.comment_section_title')}>
              <MUIInputField
                isMultiline
                value={value.clientComment ?? ''}
                onChange={(e) => patch({ clientComment: e.target.value })}
                label={t(isEditing ? 'floor_plan_step.comment_label_editing' : 'floor_plan_step.comment_label')}
              />
            </SectionedBorderBox>

          </>}

        </Stack>

      </Stack>
    </Fade>
  )
}
